// Per-day occurrence bar chart for the Chart tab's Count mode, modeled on
// BowelPane's DayBars. Bars render in one shared flex row (a fixed-height
// track) and date labels render in a separate fixed-height track below them
// (ITEM_REPORT_ENHANCEMENTS_PLAN.md D-IE-18) — previously each column was its
// own flex column, so a taller vertical label shrank that column's bar area,
// making identical counts render as different bar heights.

interface Props {
  days: { date: string; count: number }[];
  /** Formats the numeric value in each bar's tooltip (default: String(n)). */
  valueFormat?: (n: number) => string;
  /** Appended to the tooltip after the value, e.g. "mg" (default: none). */
  valueUnit?: string;
  /** Message when every value is 0 (default: "No entries in this range."). */
  emptyHint?: string;
}

export function CountChart({ days, valueFormat = String, valueUnit, emptyHint = "No entries in this range." }: Props) {
  if (days.length === 0 || days.every((d) => d.count === 0)) {
    return (
      <p className="mt-6 text-center text-sm text-gray-400 dark:text-gray-500">
        {emptyHint}
      </p>
    );
  }

  const max = Math.max(...days.map((d) => d.count), 1);

  // Every bar gets a date label, but the format/size has to shrink as more
  // bars share the same width (D-IR-5's 7/30/60/90d ranges). Past 7 bars a
  // horizontal "M/d" no longer fits its own column, so we switch to
  // vertical text (proper CSS writing-mode, not a rotated-overflow hack) so
  // labels stay legible without colliding with their neighbors.
  const n = days.length;
  const dense = n > 7;
  const labelClass = n <= 31 ? "text-[10px]" : "text-[8px]";

  return (
    <div className="w-full">
      <div className="flex h-40 w-full items-stretch gap-[2px]">
        {days.map((d) => {
          const pct = max > 0 ? (d.count / max) * 100 : 0;
          const date = new Date(d.date + "T00:00:00Z");
          const fullLabel = date.toLocaleDateString(undefined, {
            weekday: "short",
            month: "short",
            day: "numeric",
            timeZone: "UTC",
          });
          return (
            <div
              key={d.date}
              className="flex min-w-0 flex-1 items-end"
              title={`${fullLabel}: ${valueFormat(d.count)}${valueUnit ? " " + valueUnit : ""}`}
            >
              <div
                className={
                  "w-full rounded-t-md transition-all " +
                  (d.count > 0
                    ? "bg-[color:var(--accent,#2563eb)]"
                    : "bg-gray-100 dark:bg-gray-800")
                }
                style={{ height: `${Math.max(pct, d.count > 0 ? 4 : 0)}%` }}
              />
            </div>
          );
        })}
      </div>
      <div className={`mt-1.5 flex w-full gap-[2px] ${dense ? "h-10" : "h-4"}`}>
        {days.map((d) => {
          const date = new Date(d.date + "T00:00:00Z");
          const dayLabel =
            n <= 7
              ? date.toLocaleDateString(undefined, { month: "short", day: "numeric", timeZone: "UTC" })
              : `${date.getUTCMonth() + 1}/${date.getUTCDate()}`;
          return (
            <div key={d.date} className="flex min-w-0 flex-1 items-start justify-center overflow-hidden">
              <span
                className={`${labelClass} shrink-0 text-gray-400 dark:text-gray-500 ${dense ? "" : "whitespace-nowrap"}`}
                style={dense ? { writingMode: "vertical-rl", textOrientation: "sideways" } : undefined}
              >
                {dayLabel}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
